import React, { useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import './style.css'
import '../../styles/page-titles.css'
import styled, { keyframes } from 'styled-components';


import { getStoredUser } from '../../utils/safeStorage';
import { formatPriceWithSymbol, getCurrencySymbol } from '../../utils/currency';
import regionConfig from '../../config/regionConfig';

import { SidebarContext } from "../../AppRoutes";
import { registerPlanoAdmin } from "../../services/api";

import PermissionGate from "../../services/PermissionGate";

import { useNavigate } from "react-router-dom";


import { useFormik } from "formik";
import * as Yup from "yup";

import * as AiIcons from 'react-icons/ai'
import * as SlIcons from 'react-icons/sl'


import { toast } from "react-toastify";

// Keyframe animations
const slideUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const bounceIn = keyframes`
    0% {
        opacity: 0;
        transform: scale(0.3);
    }
    50% {
        transform: scale(1.05);
    }
    70% {
        transform: scale(0.9);
    }
    100% {
        opacity: 1;
        transform: scale(1);
    }
`;

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

// Main page wrapper
const PageWrapper = styled.div`
    display: flex;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background: #ffffff;
    overflow: initial;
    z-index: 9;
    position: relative;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

// Form container with card effect
const FormContainer = styled.div`
    background: #ffffff;
    border-radius: 16px;
    padding: 32px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
    animation: ${slideUp} 0.5s ease-out;
    margin-top: 20px;
`;

// Page header
const PageHeader = styled.div`
    animation: ${slideUp} 0.5s ease-out;
    margin-bottom: 24px;

    h1 {
        font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
        font-size: 28px;
        font-weight: 700;
        color: #1e293b;
        margin: 0;
        letter-spacing: -0.5px;
        position: relative;
        display: inline-block;

        &::after {
            content: '';
            position: absolute;
            bottom: -8px;
            left: 0;
            width: 40px;
            height: 3px;
            background: linear-gradient(90deg, #318cd5, #4fa3e8);
            border-radius: 2px;
        }
    }
`;

// Button group container
const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    animation: ${fadeIn} 0.5s ease-out 0.2s backwards;
`;

// Primary button (Save)
const PrimaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 24px;
    font-family: 'Segoe UI', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #318cd5, #2574b8);
    color: white;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1), 0 0 0 0 rgba(49, 140, 213, 0.4);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(180deg, rgba(255,255,255,0.15) 0%, transparent 50%);
        pointer-events: none;
    }

    &:hover {
        background: linear-gradient(135deg, #4fa3e8, #318cd5);
        transform: translateY(-2px);
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1), 0 0 20px rgba(49, 140, 213, 0.3);
    }

    &:active {
        transform: translateY(0);
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    }

    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
        transform: none;
    }

    svg {
        transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    &:hover svg {
        transform: scale(1.1);
    }

    &.loading svg {
        animation: ${spin} 0.8s linear infinite;
    }
`;

// Secondary button (Back)
const SecondaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 24px;
    font-family: 'Segoe UI', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    cursor: pointer;
    transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
    background: #ffffff;
    color: #ef4444;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    &:hover {
        border-color: #ef4444;
        background: rgba(239, 68, 68, 0.05);
        transform: translateY(-2px);
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
    }

    &:active {
        transform: translateY(0);
    }

    svg {
        transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    &:hover svg {
        transform: scale(1.1);
    }
`;

// Form row container
const FormRow = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    gap: 20px;
    flex-wrap: wrap;
    animation: ${slideUp} 0.4s ease-out backwards;
    animation-delay: ${props => props.delay || '0s'};
    margin-bottom: 24px;

    &:last-child {
        margin-bottom: 0;
    }

    @media (max-width: 1024px) {
        flex-direction: column;

        & > div {
            width: 100% !important;
        }
    }
`;

// Input box wrapper
const InputBox = styled.div`
    flex: ${props => props.flex || '1'};
    min-width: ${props => props.minWidth || '200px'};
    animation: ${fadeIn} 0.4s ease-out backwards;
    animation-delay: ${props => props.delay || '0s'};

    label {
        display: block;
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-size: 13px;
        font-weight: 600;
        color: #64748b;
        margin-bottom: 8px;
        text-transform: uppercase;
        letter-spacing: 0.5px;

        span {
            color: #ef4444;
            font-weight: 700;
        }
    }

    input, select {
        width: 100%;
        padding: 14px 16px;
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-size: 15px;
        color: #1e293b;
        background: #f8fafc;
        border: 2px solid #e2e8f0;
        border-radius: 10px;
        transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
        outline: none;

        &::placeholder {
            color: #94a3b8;
        }

        &:hover {
            border-color: #cbd5e1;
            background: #ffffff;
        }

        &:focus {
            border-color: #318cd5;
            background: #ffffff;
            box-shadow: 0 0 0 4px rgba(49, 140, 213, 0.15), 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
    }

    select {
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 12px center;
        padding-right: 44px;
        cursor: pointer;
    }

    &.has-error {
        input, select {
            border-color: #ef4444;
            background: rgba(239, 68, 68, 0.05);

            &:focus {
                box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15), 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            }
        }
    }
`;

// Error message
const ErrorMessage = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    padding: 8px 12px;
    background: rgba(239, 68, 68, 0.1);
    border-radius: 6px;
    font-size: 13px;
    color: #ef4444;
    font-weight: 500;
    animation: ${bounceIn} 0.4s ease-out;

    &::before {
        content: '!';
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        background: #ef4444;
        color: white;
        border-radius: 50%;
        font-size: 11px;
        font-weight: 700;
        flex-shrink: 0;
    }
`;

// Cycle selector container (special styling)
const CycleSelector = styled.div`
    flex: 1;
    min-width: 200px;
    animation: ${fadeIn} 0.4s ease-out backwards;
    animation-delay: ${props => props.delay || '0s'};

    .cycle-label {
        display: block;
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-size: 13px;
        font-weight: 600;
        color: #64748b;
        margin-bottom: 8px;
        text-transform: uppercase;
        letter-spacing: 0.5px;

        span {
            color: #ef4444;
            font-weight: 700;
        }
    }

    select {
        width: 100%;
        padding: 14px 16px;
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-size: 15px;
        color: #1e293b;
        background: #f8fafc;
        border: 2px solid #e2e8f0;
        border-radius: 10px;
        transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
        outline: none;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 12px center;
        padding-right: 44px;
        cursor: pointer;

        &:hover {
            border-color: #cbd5e1;
            background-color: #ffffff;
        }

        &:focus {
            border-color: #318cd5;
            background-color: #ffffff;
            box-shadow: 0 0 0 4px rgba(49, 140, 213, 0.15), 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
    }

    &.has-error select {
        border-color: #ef4444;
        background-color: rgba(239, 68, 68, 0.05);

        &:focus {
            box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15), 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
    }
`;

const PlanosAdmin = () => {
    const { t } = useTranslation('plansAdmin');
    const userParse = getStoredUser();
    const userID = userParse?._id;

    const empresa = localStorage.getItem('empresa')
    const empresaParse = JSON.parse(empresa)
    const { sidebar } = useContext(SidebarContext);

    const [selectedOption, setSelectedOption] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Options with i18n translations
    const options = [
        { value: "", label: t('add.form.selectCycle') },
        { value: "WEEKLY", label: t('add.cycles.weekly') },
        { value: "MONTHLY", label: t('add.cycles.monthly') },
        { value: "YEARLY", label: t('add.cycles.yearly') },
    ];

    //Validacoes
    const SignUpSchema = Yup.object().shape({
        title: Yup.string().required(t('add.form.required')),
        plan_identifier: Yup.string().required(t('add.form.required')),
        plan_cycle: selectedOption === "" && Yup.string().required(t('add.form.required')),
        valor_plano: Yup.number().required(t('add.form.required')),
        order: Yup.string().required(t('add.form.required')),
    });

    const formik = useFormik({
        validationSchema: SignUpSchema,
        validateOnBlur: false,
        validateOnChange: false,
        initialValues: {
            title: "",
            plan_identifier: "",
            maxPayments: "",
            valor_plano: 0,
            order: "",
        },
        onSubmit: (values) => {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log(values);

            const title = values.title;
            const plan_identifier = values.plan_identifier;
            const order = values.order;
            const plan_cycle = selectedOption;
            const maxPayments = values.maxPayments;
            const valor_plano = Number(values.valor_plano);

            handleSubmitAPI(title, plan_identifier, order, plan_cycle, maxPayments, valor_plano);
        },
    });

    const navigate = useNavigate();

    const handleBack = () => {
        navigate("/list-plans-admin");
    }

    const handleSubmitAPI = (title, plan_identifier, order, plan_cycle, maxPayments, valor_plano) => {
        setIsSubmitting(true);
        const createdBy = userID;
        const nome = title;

        process.env.REACT_APP_ISDEVELOP === 'true' && console.log("submit plano!", { nome, plan_identifier, order, plan_cycle, maxPayments, valor_plano });

        registerPlanoAdmin(createdBy, nome, plan_identifier, order, plan_cycle, maxPayments, valor_plano).then(cast => {
            if (cast.status === 201) {
                toast(t('add.toasts.success'), { autoClose: 5000, type: "success" });
                navigate("/list-plans-admin");
            }
            else {
                toast(t('add.toasts.error'), { autoClose: 5000, type: "error" });
            }
        }).catch(err => {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log('Oh noooo!!');
            toast(t('add.toasts.error'), { autoClose: 5000, type: "error" });
        }).finally(() => {
            setIsSubmitting(false);
        });
    }

    // Funcao para formatar e atualizar o valor no Formik
    const handleCurrencyChange = (e, formik) => {
        const inputValue = e.target.value;
        const rawValue = inputValue.replace(/\D/g, "");

        if (rawValue.length === 0) {
            formik.setFieldValue("valor_plano", 0);
            e.target.value = formatPriceWithSymbol(0);
            return;
        }

        const numericValue = Number(rawValue) / 100;
        e.target.value = formatPriceWithSymbol(numericValue);
        formik.setFieldValue("valor_plano", Number(rawValue) / 100);
    };

    const handleOptionChange = (event) => {
        setSelectedOption(event.target.value);
    };

    return (
        <>
            <PermissionGate permissions={['AdminOnly']}>
                <PageWrapper sidebar={sidebar}>
                    <div className="w-100 p-4 p-md-5">
                        <form onSubmit={formik.handleSubmit}>
                            <PageHeader>
                                <h1>{t('add.title')}</h1>
                            </PageHeader>

                            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "24px" }}>
                                <ButtonGroup>
                                    <PrimaryButton type="submit" disabled={isSubmitting} className={isSubmitting ? 'loading' : ''}>
                                        {isSubmitting ? (
                                            <AiIcons.AiOutlineLoading3Quarters style={{ fontSize: "20px" }} />
                                        ) : (
                                            <AiIcons.AiOutlineSave style={{ fontSize: "20px" }} />
                                        )}
                                        {t('add.buttons.save')}
                                    </PrimaryButton>

                                    <SecondaryButton type="button" onClick={handleBack}>
                                        <SlIcons.SlActionUndo style={{ fontSize: "16px" }} />
                                        {t('add.buttons.back')}
                                    </SecondaryButton>
                                </ButtonGroup>
                            </div>

                            <FormContainer>
                                <FormRow delay="0.1s">
                                    <InputBox
                                        flex="1.5"
                                        delay="0.1s"
                                        className={formik.errors.title ? 'has-error' : ''}
                                    >
                                        <label>{t('add.form.planName')}<span>*</span></label>
                                        <input
                                            placeholder={t('add.form.planNamePlaceholder')}
                                            id="title"
                                            type="text"
                                            name="title"
                                            onChange={formik.handleChange}
                                            value={formik.values.title}
                                        />
                                        {formik.errors.title && (
                                            <ErrorMessage>{formik.errors.title}</ErrorMessage>
                                        )}
                                    </InputBox>

                                    <InputBox
                                        flex="1.5"
                                        delay="0.15s"
                                        className={formik.errors.plan_identifier ? 'has-error' : ''}
                                    >
                                        <label>{t('add.form.planIdentifier')}<span>*</span></label>
                                        <input
                                            placeholder={t('add.form.planIdentifierPlaceholder')}
                                            id="plan_identifier"
                                            type="text"
                                            name="plan_identifier"
                                            onChange={formik.handleChange}
                                            value={formik.values.plan_identifier}
                                        />
                                        {formik.errors.plan_identifier && (
                                            <ErrorMessage>{formik.errors.plan_identifier}</ErrorMessage>
                                        )}
                                    </InputBox>

                                    <InputBox
                                        flex="0.8"
                                        minWidth="120px"
                                        delay="0.2s"
                                        className={formik.errors.order ? 'has-error' : ''}
                                    >
                                        <label>{t('add.form.order')}<span>*</span></label>
                                        <input
                                            placeholder={t('add.form.orderPlaceholder')}
                                            id="order"
                                            type="number"
                                            name="order"
                                            onChange={formik.handleChange}
                                            value={formik.values.order}
                                        />
                                        {formik.errors.order && (
                                            <ErrorMessage>{formik.errors.order}</ErrorMessage>
                                        )}
                                    </InputBox>
                                </FormRow>

                                <FormRow delay="0.2s">
                                    <CycleSelector
                                        delay="0.25s"
                                        className={formik.errors.plan_cycle ? 'has-error' : ''}
                                    >
                                        <label className="cycle-label">{t('add.form.planCycle')}<span>*</span></label>
                                        <select
                                            value={selectedOption}
                                            onChange={(e) => {
                                                handleOptionChange(e);
                                                formik.setFieldValue("plan_cycle", e.target.value);
                                            }}
                                        >
                                            {options.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        {formik.errors.plan_cycle && (
                                            <ErrorMessage>{formik.errors.plan_cycle}</ErrorMessage>
                                        )}
                                    </CycleSelector>

                                    <InputBox
                                        flex="1"
                                        delay="0.3s"
                                    >
                                        <label>{t('add.form.maxCharges')}</label>
                                        <input
                                            placeholder={t('add.form.maxChargesPlaceholder')}
                                            id="maxPayments"
                                            type="number"
                                            name="maxPayments"
                                            onChange={formik.handleChange}
                                            value={formik.values.maxPayments}
                                        />
                                        {formik.errors.maxPayments && (
                                            <ErrorMessage>{formik.errors.maxPayments}</ErrorMessage>
                                        )}
                                    </InputBox>

                                    <InputBox
                                        flex="1"
                                        delay="0.35s"
                                        className={formik.errors.valor_plano ? 'has-error' : ''}
                                    >
                                        <label>{t('add.form.value')}<span>*</span></label>
                                        <input
                                            placeholder={t('add.form.valuePlaceholder')}
                                            id="valor_plano"
                                            type="text"
                                            name="valor_plano"
                                            onChange={(e) => handleCurrencyChange(e, formik)}
                                            value={
                                                formik.values.valor_plano >= 0
                                                    ? formatPriceWithSymbol(formik.values.valor_plano)
                                                    : ""
                                            }
                                        />
                                        {formik.errors.valor_plano && (
                                            <ErrorMessage>{formik.errors.valor_plano}</ErrorMessage>
                                        )}
                                    </InputBox>
                                </FormRow>
                            </FormContainer>
                        </form>
                    </div>
                </PageWrapper>
            </PermissionGate>
        </>
    );
};

export default PlanosAdmin;
